/**
 * Buy-the-Look Bundle — Landingpage Custom Element
 * Orchestriert n Bundle-Cards (Variant-Picker, Preis, Verfügbarkeit) und macht
 * einen einzigen Batch-Add an /cart/add.js. Dispatcht CartAddEvent damit der
 * Cart-Drawer automatisch öffnet (analog product-form.js).
 *
 * Source of Truth Phase 1: Section-Blocks (Server-rendered).
 * Source of Truth Phase 2: Metaobject buy_the_look_bundle (Section refactor).
 * Discount: appliziert sich Server-seitig (Automatic Discount Phase 1,
 * Shopify Function Phase 2). Frontend zeigt nur die visuelle Vorschau.
 */

import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

const SOURCE_TAG = 'lora-buy-the-look';

class LoraBuyTheLook extends HTMLElement {
  connectedCallback() {
    this._cards = Array.from(this.querySelectorAll('[data-btl-card]'));
    if (this._cards.length === 0) return;

    this._discountType = this.dataset.discountType || 'percent';
    this._discountValue = parseFloat(this.dataset.discountValue) || 0;
    this._moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this._currency = this.dataset.currency || 'EUR';

    this._addBtn = this.querySelector('[data-btl-add]');
    this._addBtnLabel = this.querySelector('[data-btl-add-label]');
    this._spinner = this.querySelector('[data-btl-spinner]');
    this._messageEl = this.querySelector('[data-btl-message]');

    this._sumIndividualEl = this.querySelector('[data-sum-individual]');
    this._sumBundleEl = this.querySelector('[data-sum-bundle]');
    this._sumSavingsEl = this.querySelector('[data-sum-savings]');
    this._sumSavingsPctEl = this.querySelector('[data-savings-pct]');

    // Shipping-Savings vom Server-Render lesen (Liquid setzt das Attribut).
    // Wird bei Variant-Wechsel ebenfalls dazuaddiert.
    this._shippingSavings = parseInt(
      this._sumSavingsEl?.dataset.shippingSavings || '0',
      10,
    ) || 0;
    this._freeShipping = this._shippingSavings > 0;

    this._summaryEl = this.querySelector('[data-summary]');
    this._toggleBtn = this.querySelector('[data-btl-toggle]');
    this._compactPriceEl = this.querySelector('[data-btl-compact-price]');
    this._compactSavingsEl = this.querySelector('[data-btl-compact-savings]');

    this._initCards();
    this._onAddClick = this._onAddClick.bind(this);
    this._onToggleClick = this._onToggleClick.bind(this);
    this._addBtn?.addEventListener('click', this._onAddClick);
    this._toggleBtn?.addEventListener('click', this._onToggleClick);

    this._updateSummary();
  }

  _onToggleClick() {
    if (!this._summaryEl) return;
    const isExpanded = this._summaryEl.dataset.expanded === 'true';
    this._summaryEl.dataset.expanded = isExpanded ? 'false' : 'true';
    this._toggleBtn?.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
  }

  disconnectedCallback() {
    this._addBtn?.removeEventListener('click', this._onAddClick);
    this._toggleBtn?.removeEventListener('click', this._onToggleClick);
    this._cards.forEach((card) => {
      const radios = card.querySelectorAll('[data-btl-option]');
      radios.forEach((r) => r.removeEventListener('change', this._cardListeners?.get(card)));
    });
  }

  _initCards() {
    this._cardListeners = new WeakMap();

    for (const card of this._cards) {
      const isV2 = card.classList.contains('btl-card--v2');
      card._btlV2 = isV2;

      // v2: pro Card mehrere Option-Blöcke mit jeweils einer Variants-JSON
      // v1: nur ein Variants-JSON
      const optionBlocks = Array.from(card.querySelectorAll('[data-btl-option-block]'));
      card._btlOptionBlocks = optionBlocks;
      const variantsPerOption = optionBlocks.map((block) => {
        const script = block.querySelector('[data-btl-variants]');
        try {
          return script ? JSON.parse(script.textContent || '[]') : [];
        } catch (e) {
          console.warn('[btl] variants JSON parse failed', e);
          return [];
        }
      });
      card._btlVariantsPerOption = variantsPerOption;

      // Fallback für v1-Cards (kein [data-btl-option-block])
      if (variantsPerOption.length === 0) {
        const fallbackScript = card.querySelector('[data-btl-variants]');
        try {
          variantsPerOption.push(fallbackScript ? JSON.parse(fallbackScript.textContent || '[]') : []);
        } catch (e) {
          console.warn('[btl] variants JSON parse failed', e);
          variantsPerOption.push([]);
        }
      }

      // Options-Metadaten (v2): [{product_id, product_title, product_url, quantity, price_override_*, default_variant_id}]
      const optsScript = card.querySelector('[data-btl-options]');
      let options = [];
      try {
        options = optsScript ? JSON.parse(optsScript.textContent || '[]') : [];
      } catch (e) {
        console.warn('[btl] options JSON parse failed', e);
      }
      card._btlOptions = options;
      card._btlSelectedOptionIndex = parseInt(card.dataset.defaultOptionIndex, 10) || 0;
      card._btlIsOptional = card.dataset.isOptional === 'true';
      card._btlOptionalActive = card.dataset.optionalActive !== 'false';

      // Initial-Quantity: v2 = aus aktiver Option, v1 = aus card.dataset.quantity
      const activeOption = options[card._btlSelectedOptionIndex];
      card._btlQuantity = activeOption?.quantity || parseInt(card.dataset.quantity, 10) || 1;

      card._btlSelectedVariantId = card.dataset.initialVariantId || '';
      card._btlUnitPrice = parseInt(card.dataset.initialPrice, 10) || 0;
      // Effective Unit (mit Override): aus Liquid vorberechnet, JS aktualisiert bei Wechsel
      card._btlEffectiveUnitPrice = parseInt(card.dataset.initialUnitPrice, 10) || card._btlUnitPrice;
      card._btlAvailable = card.dataset.initialAvailable === 'true';

      // Variant-Radio-Listener auf alle Option-Blöcke registrieren
      const radios = card.querySelectorAll('[data-btl-option]');
      const handler = () => this._onCardVariantChange(card);
      this._cardListeners.set(card, handler);
      radios.forEach((r) => r.addEventListener('change', handler));

      // Option-Dropdown
      const optionSelect = card.querySelector('[data-btl-option-select]');
      if (optionSelect) {
        const selectHandler = () => this._onOptionSelectChange(card, parseInt(optionSelect.value, 10) || 0);
        optionSelect.addEventListener('change', selectHandler);
      }

      // Optional-Toggle
      const optToggle = card.querySelector('[data-btl-optional-toggle]');
      if (optToggle) {
        optToggle.checked = card._btlOptionalActive;
        const toggleHandler = () => {
          card._btlOptionalActive = optToggle.checked;
          card.dataset.optionalActive = optToggle.checked ? 'true' : 'false';
          card.classList.toggle('btl-card--optional-inactive', !optToggle.checked);
          this._updateSummary();
        };
        optToggle.addEventListener('change', toggleHandler);
        // Apply initial state
        card.classList.toggle('btl-card--optional-inactive', !card._btlOptionalActive);
      }
    }
  }

  _activeVariantsForCard(card) {
    return card._btlVariantsPerOption?.[card._btlSelectedOptionIndex] ?? [];
  }

  _activeOptionMeta(card) {
    return card._btlOptions?.[card._btlSelectedOptionIndex] ?? null;
  }

  _onOptionSelectChange(card, newIndex) {
    if (newIndex === card._btlSelectedOptionIndex) return;
    card._btlSelectedOptionIndex = newIndex;

    // Variant-Picker-Blöcke umschalten
    (card._btlOptionBlocks || []).forEach((block, i) => {
      block.toggleAttribute('hidden', i !== newIndex);
    });

    const meta = this._activeOptionMeta(card);
    if (meta) {
      card._btlQuantity = meta.quantity || 1;
      card.dataset.quantity = String(card._btlQuantity);
      // Produktlink + Title aktualisieren
      const linkEl = card.querySelector('[data-btl-product-link]');
      if (linkEl && meta.product_url && meta.product_title) {
        linkEl.href = meta.product_url;
        linkEl.textContent = meta.product_title;
      }
      // Qty-Badge
      let qtyBadge = card.querySelector('[data-btl-qty-badge]');
      if (card._btlQuantity > 1) {
        if (!qtyBadge) {
          qtyBadge = document.createElement('span');
          qtyBadge.className = 'btl-card__qty-badge';
          qtyBadge.setAttribute('aria-label', 'Im Bundle enthalten');
          qtyBadge.dataset.btlQtyBadge = '';
          card.querySelector('.btl-card__media')?.prepend(qtyBadge);
        }
        qtyBadge.innerHTML = `×&nbsp;${card._btlQuantity}`;
      } else if (qtyBadge) {
        qtyBadge.remove();
      }
    }

    // Nach Option-Wechsel: aktive Variant initial = default oder erste available
    this._onCardVariantChange(card);
  }

  _computeEffectiveUnit(variantPrice, optionMeta) {
    if (!optionMeta) return variantPrice;
    if (optionMeta.price_override_amount != null) {
      return Math.max(0, Math.round(optionMeta.price_override_amount));
    }
    if (optionMeta.price_override_percent != null) {
      const pct = Math.max(0, Math.min(100, optionMeta.price_override_percent));
      return Math.round((variantPrice * (100 - pct)) / 100);
    }
    return variantPrice;
  }

  _onCardVariantChange(card) {
    // Variant-Radios sind die im aktiven Option-Block
    const activeBlock = card._btlOptionBlocks?.[card._btlSelectedOptionIndex] ?? card;
    const selectedOptions = this._collectSelectedOptions(activeBlock);
    const variants = this._activeVariantsForCard(card);
    const variant = this._matchVariant(variants, selectedOptions);

    if (!variant) {
      card._btlSelectedVariantId = '';
      card._btlAvailable = false;
      this._setCardUnavailable(card, 'Diese Kombination ist nicht verfügbar');
    } else {
      card._btlSelectedVariantId = String(variant.id);
      card._btlUnitPrice = variant.price;
      const meta = this._activeOptionMeta(card);
      card._btlEffectiveUnitPrice = this._computeEffectiveUnit(variant.price, meta);
      card._btlAvailable = variant.available;
      this._setCardPrice(card, card._btlEffectiveUnitPrice);
      this._setCardAvailable(card, variant.available);
      this._setCardImage(card, variant);
      this._updateSelectedLabels(activeBlock, selectedOptions);
    }
    this._updateSummary();
  }

  _setCardImage(card, variant) {
    if (!variant?.image_url) return;
    const img = card.querySelector('[data-btl-img]');
    if (!img) return;
    if (img.src.split('?')[0] === variant.image_url.split('?')[0]) return;
    img.src = variant.image_url;
    if (variant.image_alt) img.alt = variant.image_alt;
    // srcset wegnehmen damit Browser den neuen src nutzt
    img.removeAttribute('srcset');
  }

  _collectSelectedOptions(card) {
    const radios = card.querySelectorAll('[data-btl-option]:checked');
    const opts = [];
    radios.forEach((r) => {
      const optIdx = parseInt(r.dataset.optionIndex, 10);
      opts[optIdx] = r.value;
    });
    return opts;
  }

  _matchVariant(variants, selectedOptions) {
    return variants.find((v) =>
      v.options.every((val, idx) => val === selectedOptions[idx])
    );
  }

  _setCardPrice(card, priceCents) {
    const priceEl = card.querySelector('[data-btl-price]');
    if (priceEl) {
      priceEl.textContent = this._money(priceCents);
      priceEl.dataset.unitPrice = String(priceCents);
    }
    const lineTotalEl = card.querySelector('[data-btl-line-total]');
    if (lineTotalEl) {
      lineTotalEl.textContent = this._money(priceCents * card._btlQuantity);
    }
    const unitDisplayEl = card.querySelector('[data-btl-unit-display]');
    if (unitDisplayEl) {
      unitDisplayEl.textContent = this._money(priceCents);
    }
    const qtyDisplayEl = card.querySelector('[data-btl-qty-display]');
    if (qtyDisplayEl) {
      qtyDisplayEl.textContent = String(card._btlQuantity);
    }
    const priceMetaEl = card.querySelector('[data-btl-price-meta]');
    if (priceMetaEl) {
      priceMetaEl.toggleAttribute('hidden', card._btlQuantity <= 1);
    }
  }

  _setCardAvailable(card, available) {
    card.classList.toggle('btl-card--unavailable', !available);
    let soldOutBadge = card.querySelector('.btl-card__sold-out');
    if (!available && !soldOutBadge) {
      soldOutBadge = document.createElement('span');
      soldOutBadge.className = 'btl-card__sold-out';
      soldOutBadge.textContent = 'Nicht verfügbar';
      card.querySelector('.btl-card__media')?.appendChild(soldOutBadge);
    } else if (available && soldOutBadge) {
      soldOutBadge.remove();
    }
  }

  _setCardUnavailable(card, message) {
    this._setCardAvailable(card, false);
    const priceEl = card.querySelector('[data-btl-price]');
    if (priceEl) priceEl.textContent = message;
  }

  _updateSelectedLabels(card, selectedOptions) {
    selectedOptions.forEach((val, idx) => {
      const labelEl = card.querySelector(`[data-btl-selected-${idx}]`);
      if (labelEl) labelEl.innerHTML = `:&nbsp;${this._escape(val)}`;
    });
  }

  _escape(str) {
    return String(str).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );
  }

  _money(cents) {
    return formatMoney(cents, this._moneyFormat, this._currency);
  }

  _computeTotals() {
    // sum_individual_full = was die Produkte EINZELN (ohne jeden Rabatt) kosten würden
    // sum_bundle = was der Kunde im Bundle bezahlt (Per-Option-Override + Bundle-Default)
    let sumIndividualFull = 0;
    let sumBundle = 0;
    let anyUnavailable = false;
    let missingSelection = false;

    for (const card of this._cards) {
      if (card._btlIsOptional && !card._btlOptionalActive) continue; // optionaler Slot deaktiviert
      if (!card._btlSelectedVariantId) missingSelection = true;
      if (!card._btlAvailable) anyUnavailable = true;
      const qty = card._btlQuantity || 1;
      const fullPrice = (card._btlUnitPrice || 0) * qty;
      const effective = (card._btlEffectiveUnitPrice ?? card._btlUnitPrice ?? 0) * qty;
      sumIndividualFull += fullPrice;

      // Effective = mit Per-Option-Override. Wenn Override greift, ist das schon der Bundle-Preis dieser Line.
      // Wenn keiner greift, appliziert sich der Bundle-Default-Discount.
      const hasOptionOverride = effective !== fullPrice;
      if (hasOptionOverride) {
        sumBundle += effective;
      } else if (this._discountValue > 0) {
        if (this._discountType === 'percent') {
          sumBundle += Math.round((fullPrice * (100 - this._discountValue)) / 100);
        } else {
          // Bundle-fixed_amount: gilt einmalig fürs gesamte Bundle, applizieren wir am Ende
          sumBundle += fullPrice;
        }
      } else {
        sumBundle += fullPrice;
      }
    }
    // Bundle-fixed_amount einmalig abziehen (nur wenn discount_type='fixed_amount')
    if (this._discountValue > 0 && this._discountType !== 'percent') {
      sumBundle = Math.max(0, sumBundle - this._discountValue * 100);
    }

    const productSavings = Math.max(0, sumIndividualFull - sumBundle);
    const shippingSavings = this._shippingSavings || 0;
    const savings = productSavings + shippingSavings;
    const savingsPct = sumIndividualFull > 0 ? Math.round((productSavings * 100) / sumIndividualFull) : 0;

    return {
      sumIndividual: sumIndividualFull,
      sumBundle,
      savings,
      productSavings,
      shippingSavings,
      savingsPct,
      anyUnavailable,
      missingSelection,
    };
  }

  _updateSummary() {
    const t = this._computeTotals();

    if (this._sumIndividualEl) {
      this._sumIndividualEl.textContent = this._money(t.sumIndividual);
      this._sumIndividualEl.dataset.sumIndividual = String(t.sumIndividual);
    }
    if (this._sumBundleEl) {
      this._sumBundleEl.textContent = this._money(t.sumBundle);
      this._sumBundleEl.dataset.sumBundle = String(t.sumBundle);
    }
    if (this._sumSavingsEl) {
      this._sumSavingsEl.textContent = this._money(t.savings);
      this._sumSavingsEl.dataset.sumSavings = String(t.savings);
      this._sumSavingsEl.dataset.productSavings = String(t.productSavings);
      this._sumSavingsEl.dataset.shippingSavings = String(t.shippingSavings);
    }
    if (this._sumSavingsPctEl) {
      if (this._freeShipping) {
        this._sumSavingsPctEl.innerHTML = `(${this._money(t.productSavings)} Rabatt + ${this._money(t.shippingSavings)} Versand)`;
      } else {
        this._sumSavingsPctEl.innerHTML = `(−${t.savingsPct} %)`;
      }
    }

    if (this._compactPriceEl) {
      this._compactPriceEl.textContent = this._money(t.sumBundle);
    }
    if (this._compactSavingsEl) {
      this._compactSavingsEl.textContent = `spart ${this._money(t.savings)}`;
    }

    const disabled = t.anyUnavailable || t.missingSelection || this._cards.length === 0;
    if (this._addBtn) {
      this._addBtn.disabled = disabled;
      if (this._addBtnLabel) {
        if (t.anyUnavailable) {
          this._addBtnLabel.textContent = 'Aktuell nicht verfügbar';
        } else if (t.missingSelection) {
          this._addBtnLabel.textContent = 'Bitte alle Optionen wählen';
        } else {
          this._addBtnLabel.textContent = 'Alle in den Warenkorb';
        }
      }
    }
  }

  _collectCartSectionIds() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const ids = [];
    for (const c of cartItemsComponents) {
      if (c instanceof HTMLElement && c.dataset.sectionId) {
        ids.push(c.dataset.sectionId);
      }
    }
    return ids.join(',');
  }

  async _onAddClick(event) {
    event?.preventDefault();
    if (this._addBtn?.disabled) return;

    let items = this._cards
      .filter((card) => {
        if (card._btlIsOptional && !card._btlOptionalActive) return false;
        return card._btlSelectedVariantId && card._btlAvailable;
      })
      .map((card) => {
        const props = {
          _bundle_handle: this.dataset.bundleHandle || this.dataset.pageHandle || '',
          _bundle_name: this.dataset.bundleName || '',
          _bundle_source: SOURCE_TAG,
        };
        if (card.dataset.slotId) {
          props._bundle_slot_id = card.dataset.slotId;
        }
        return {
          id: Number(card._btlSelectedVariantId),
          quantity: card._btlQuantity || 1,
          properties: props,
        };
      });

    if (items.length === 0) return;

    // Dedup: Varianten, die bereits im Warenkorb liegen (egal wie hineingekommen,
    // z.B. als Gratis-Geschenk), NICHT erneut hinzufügen.
    try {
      const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await cartRes.json();
      const inCart = new Set((cart.items || []).map((i) => Number(i.variant_id)));
      items = items.filter((it) => !inCart.has(Number(it.id)));
    } catch (e) {
      // Konservativ: bei Fehler wie bisher fortfahren (kein harter Block).
    }
    if (items.length === 0) {
      this._showMessage('Alles aus dem Look ist bereits im Warenkorb.', false);
      return;
    }

    this._setLoading(true);
    this._showMessage('', false);

    const url = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    const payload = { items, sections: this._collectCartSectionIds() };

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.status) {
          this._showMessage(
            response.message || 'Hinzufügen fehlgeschlagen. Bitte erneut versuchen.',
            true
          );
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: SOURCE_TAG,
              itemCount: items.reduce((s, i) => s + i.quantity, 0),
            })
          );
          return;
        }

        this._showMessage('Look im Warenkorb. Rabatt wird automatisch abgezogen.', false);

        document.dispatchEvent(
          new CartAddEvent(response, this.id, {
            source: SOURCE_TAG,
            itemCount: items.reduce((s, i) => s + i.quantity, 0),
            sections: response.sections,
          })
        );

        this._trackAnalytics(items);
      })
      .catch((err) => {
        console.error('[buy-the-look] add failed', err);
        this._showMessage('Verbindung fehlgeschlagen. Bitte erneut versuchen.', true);
      })
      .finally(() => this._setLoading(false));
  }

  _setLoading(loading) {
    if (this._addBtn) this._addBtn.disabled = loading || this._isAnyUnavailable();
    this._spinner?.toggleAttribute('hidden', !loading);
    if (this._addBtnLabel) {
      this._addBtnLabel.hidden = loading;
    }
  }

  _isAnyUnavailable() {
    return this._cards.some((c) => !c._btlAvailable || !c._btlSelectedVariantId);
  }

  _showMessage(text, isError) {
    if (!this._messageEl) return;
    if (!text) {
      this._messageEl.hidden = true;
      this._messageEl.textContent = '';
      return;
    }
    this._messageEl.hidden = false;
    this._messageEl.textContent = text;
    this._messageEl.classList.toggle('btl__summary-message--error', !!isError);
  }

  _trackAnalytics(items) {
    const t = this._computeTotals();
    const detail = {
      bundle_handle: this.dataset.pageHandle || '',
      bundle_name: this.dataset.bundleName || '',
      item_count: items.length,
      total_quantity: items.reduce((s, i) => s + i.quantity, 0),
      value_individual_cents: t.sumIndividual,
      value_bundle_cents: t.sumBundle,
      savings_cents: t.savings,
      savings_pct: t.savingsPct,
      currency: this._currency,
      variant_ids: items.map((i) => String(i.id)),
    };

    document.dispatchEvent(new CustomEvent('lora-btl:add', { detail, bubbles: true }));

    window.Shopify?.analytics?.publish?.('lora_buy_the_look_add', detail);
  }
}

if (!customElements.get('lora-buy-the-look')) {
  customElements.define('lora-buy-the-look', LoraBuyTheLook);
}
